import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ServerCrash } from 'lucide-react';
import { ErrorState } from '../../components/ui/ErrorState';
import { connectionRetried } from '../../features/connection/connectionSlice';

/**
 * The one screen that takes over everything.
 *
 * It appears only when a request got **no answer** — network down, or a gateway
 * in front of a dead application. That case has no owner: from inside a
 * component a dead backend and a broken endpoint are the same failed promise, so
 * nobody down there can say which it was. Everything the server does answer,
 * including a 404 or a 500, stays where it happened, and so does a request that
 * was only too slow.
 *
 * It also covers the login, which is the only place with no component mounted to
 * report anything at all — today a dead backend there produces the worst message
 * in the app: "Credenciales Inválidas".
 *
 * Nothing here asks the server anything. "Reintentar" goes back to the app, and
 * the requests the app makes anyway answer the question: if the next one gets no
 * answer either, this screen comes back and says so.
 */
export const ApiUnavailable = () => {
  const dispatch = useDispatch();
  const stillUnreachable = useSelector((state) => state.connection.stillUnreachable);

  return (
    <div className="min-h-screen bg-ninja-gray dark:bg-[#0f0f0f] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-xl border border-border">
        <ErrorState
          icon={ServerCrash}
          title="Sin conexión con el servidor"
          description={
            stillUnreachable
              ? 'El servidor sigue sin responder. Puede estar reiniciándose; vuelve a intentarlo en un momento.'
              : 'No pudimos comunicarnos con NinjaHubs. Revisa tu conexión a internet; si está bien, el servidor puede estar caído.'
          }
          onRetry={() => dispatch(connectionRetried())}
        />
      </div>
    </div>
  );
};
